import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarIcon, Search, X, RefreshCw, FileSpreadsheet, FileText,
  Eye, Receipt, Shield, Gem, CheckSquare2, Square
} from 'lucide-react';
import { saleService } from '@/services/sale.service';
import { clientService } from '@/services/client.service';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import { GarantiaModal } from '@/components/GarantiaModal';
import { ReceiptModal } from '@/components/ReceiptModal';
import { WarrantyModal } from '@/components/WarrantyModal';
import { Sale, CodigoPeca } from '@/pages/MinhasVendas';

// Interface estendida para vendas com dados do admin
interface SaleWithUser extends Sale {
  idpagarme?: string;
  diamonds_revendedora?: number;
  diamonds_cliente?: number;
  client_id?: number;
  user?: {
    id: number;
    nome_razao_social: string;
    codigo: string;
  };
  nome_revendedora?: string;
  codUser?: string;
}

// Helpers
const formatCurrency = (amountInCents: number): string => {
  return (amountInCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const mapPaymentMethod = (method: string): string => {
  switch (method) {
    case 'credit_card': return 'Cartão de Crédito';
    case 'pix': return 'Pix';
    default: return 'Não identificado';
  }
};

const formatDate = (dateString: string): string => {
  return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
};

const getDisplayCode = (sale: SaleWithUser): string => {
  return sale.codepagarme || `XDINF${sale.id}`;
};

const getSituationStyles = (situation?: string) => {
  switch (situation?.toLowerCase()) {
    case 'pendente':
      return { badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', row: 'bg-yellow-50' };
    case 'aprovado':
      return { badge: 'bg-blue-100 text-blue-800 border-blue-300', row: 'bg-blue-50' };
    case 'reprovado':
      return { badge: 'bg-red-100 text-red-800 border-red-300', row: 'bg-red-50' };
    case 'baixado':
      return { badge: 'bg-green-100 text-green-800 border-green-300', row: 'bg-green-50' };
    default:
      return { badge: 'bg-gray-100 text-gray-800 border-gray-300', row: '' };
  }
};

export default function RelatoriosVendas() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [sales, setSales] = useState<SaleWithUser[]>([]);
  const [totalSales, setTotalSales] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [situationFilter, setSituationFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Debounce da busca
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seleção em massa
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Modais
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [garantiaModalOpen, setGarantiaModalOpen] = useState(false);
  const [warrantyModalOpen, setWarrantyModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [saleForGarantia, setSaleForGarantia] = useState<Sale | null>(null);
  const [garantiaFormData, setGarantiaFormData] = useState({
    cpf_cliente: '',
    telefone_cliente: '',
    nome_cliente: '',
    codigos: [] as CodigoPeca[],
    idcliente: null as number | null,
    sale: {} as any,
    iduser: null as number | null,
    sale_date_created: ''
  });
  const [foundClient, setFoundClient] = useState(false);
  const [isSubmittingGarantia, setIsSubmittingGarantia] = useState(false);

  // Bloquear acesso para user.id === 969
  useEffect(() => {
    if (user?.id === 969) {
      navigate('/home');
    }
  }, [user]);

  // Carrega vendas com paginação server-side
  const loadSales = useCallback(async (
    page = currentPage,
    limit = pageSize,
    search = searchFilter,
    start = startDate,
    end = endDate,
    situation = situationFilter,
    client = clientFilter,
  ) => {
    try {
      const params: any = { page, limit };
      if (search) params.search = search;
      if (situation) params.situation = situation;
      if (client) params.clientFilter = client;
      if (start && end) {
        params.dateStart = format(start, 'yyyy-MM-dd');
        params.dateEnd = format(end, 'yyyy-MM-dd 23:59:59');
      }

      const response = await saleService.findAllSalesWithUserDataPaginated(params);
      if (response && response.data) {
        response.data.forEach((sale: SaleWithUser) => {
          sale.nome_revendedora = sale.user?.nome_razao_social;
          sale.codUser = sale.user?.codigo;
        });
        setSales(response.data);
        setTotalSales(response.total);
      } else {
        setSales([]);
        setTotalSales(0);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
      toast.error('Erro ao buscar relatório de vendas');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchFilter, startDate, endDate, situationFilter, clientFilter]);

  useEffect(() => {
    loadSales();
  }, [currentPage, pageSize]);

  const totalPages = Math.ceil(totalSales / pageSize);

  const atualizarDados = async () => {
    await loadSales(1, pageSize, '', undefined, undefined, '', '');
    setSearchFilter('');
    setStartDate(undefined);
    setEndDate(undefined);
    setSituationFilter('');
    setClientFilter('');
    setCurrentPage(1);
    toast.success('Dados atualizados!');
  };

  // ---- Filtros (server-side) ----
  const applyFilter = (value: string) => {
    setSearchFilter(value);
    // Debounce: aguarda 500ms antes de buscar no servidor
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      loadSales(1, pageSize, value, startDate, endDate, situationFilter, clientFilter);
    }, 1500);
  };

  const aplicarFiltroPorPeriodo = () => {
    if (startDate && endDate) {
      setCurrentPage(1);
      loadSales(1, pageSize, searchFilter, startDate, endDate, situationFilter, clientFilter);
    }
  };

  const limparDatas = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchFilter('');
    setSituationFilter('');
    setClientFilter('');
    setCurrentPage(1);
    loadSales(1, pageSize, '', undefined, undefined, '', '');
  };

  // Seleção em massa
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isAllPageSelected = sales.length > 0 && sales.every(s => selectedIds.has(s.id));

  const toggleSelectAllPage = () => {
    if (isAllPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        sales.forEach(s => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        sales.forEach(s => next.add(s.id));
        return next;
      });
    }
  };

  const onBulkAction = async (situation: string) => {
    if (user?.role !== 'admin') {
      toast.error('Somente administradores podem alterar vendas em massa');
      return;
    }
    if (selectedIds.size === 0) return;

    if (situation !== 'baixado') {
      const hasBaixado = sales.some(s => selectedIds.has(s.id) && s.situation === 'baixado');
      if (hasBaixado) {
        const result = await Swal.fire({
          title: 'Atenção!',
          text: `Uma ou mais vendas selecionadas estão com status "baixado". Deseja realmente alterá-las para "${situation}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, alterar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#d33',
        });
        if (!result.isConfirmed) return;
      }
    }

    try {
      setIsBulkLoading(true);
      const resp = await saleService.updateSaleSituationBulk(Array.from(selectedIds), situation);
      toast.success(resp?.message ?? 'Vendas atualizadas com sucesso');
      setSelectedIds(new Set());
      await loadSales();
    } catch (error) {
      console.error('Erro ao atualizar vendas em massa:', error);
      toast.error('Erro ao atualizar vendas em massa');
    } finally {
      setIsBulkLoading(false);
    }
  };

  // ---- Situação ----
  const onSituacaoChange = async (saleId: number, newSituation: string) => {
    if (user?.role !== 'admin' && (newSituation === 'baixado' || newSituation === 'aprovado')) {
      Swal.fire({
        title: 'Erro!',
        text: 'Somente administradores podem alterar a situação de vendas para baixado ou aprovado',
        icon: 'error',
        confirmButtonText: 'Ok',
      });
      return;
    }

    try {
      const response = await saleService.updateSaleSituation(saleId, newSituation);
      if (response) {
        toast.success('Situação atualizada com sucesso');
        setSales(prev => prev.map(s => s.id === saleId ? { ...s, situation: newSituation } : s));
      }
    } catch (error) {
      console.error('Erro ao atualizar situação:', error);
      toast.error('Erro ao atualizar situação');
    }
  };

  // ---- Exportação (busca todos os dados para exportar) ----
  const fetchAllForExport = async (): Promise<SaleWithUser[]> => {
    try {
      const response = await saleService.findAllSalesWithUserData();
      if (response && Array.isArray(response)) {
        response.forEach((sale: SaleWithUser) => {
          sale.nome_revendedora = sale.user?.nome_razao_social;
          sale.codUser = sale.user?.codigo;
        });
        return response;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar vendas para exportação:', error);
      toast.error('Erro ao buscar dados para exportação');
      return [];
    }
  };

  const exportPdf = async () => {
    try {
      toast.info('Preparando exportação PDF...');
      const allSales = await fetchAllForExport();
      if (allSales.length === 0) {
        toast.error('Nenhuma venda encontrada para exportar');
        return;
      }

      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');

      const doc = new jsPDF('landscape');
      const columns = ['Código', 'Mét. Pagamento', 'Valor', 'Revendedora', 'Acerto', 'Data'];
      const body = allSales.map(row => [
        getDisplayCode(row),
        mapPaymentMethod(row.payment_method),
        formatCurrency(row.amount),
        row.nome_revendedora || '-',
        row.situation || '-',
        formatDate(row.date_created),
      ]);

      (doc as any).autoTable({
        theme: 'grid',
        head: [columns],
        body,
      });

      doc.save(`Relatorio_Vendas_${format(new Date(), 'dd-MM-yyyy-HH-mm')}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF. Verifique se as dependências estão instaladas.');
    }
  };

  const exportExcel = async () => {
    try {
      toast.info('Preparando exportação Excel...');
      const allSales = await fetchAllForExport();
      if (allSales.length === 0) {
        toast.error('Nenhuma venda encontrada para exportar');
        return;
      }

      const XLSX = await import('xlsx');

      const data = allSales.map(row => ({
        'Código': getDisplayCode(row),
        'Mét. Pagamento': mapPaymentMethod(row.payment_method),
        'Valor': formatCurrency(row.amount),
        'Revendedora': row.nome_revendedora || '-',
        'Cliente': row.nome_cliente || 'Não informado',
        'Acerto': row.situation || '-',
        'Data': formatDate(row.date_created),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendas');
      XLSX.writeFile(wb, `Relatorio_Vendas_${format(new Date(), 'dd-MM-yyyy-HH-mm')}.xlsx`);
      toast.success('Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar Excel. Verifique se as dependências estão instaladas.');
    }
  };

  // ---- Recibo ----
  const downloadReceipt = async () => {
    if (!selectedSale) return;
    const element = document.getElementById('receipt-content');
    if (!element) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(element, { useCORS: true, allowTaint: true, scale: 2 } as any);      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `Recibo_Compra_${selectedSale.codepagarme || `XDINF${selectedSale.id}`}.png`;
      link.click();
      toast.success('Recibo baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar recibo:', error);
      toast.error('Erro ao gerar recibo');
    }
  };

  // ---- Garantia ----
  const openGarantiaModal = (sale: SaleWithUser) => {
    setSaleForGarantia(sale as Sale);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: sale.nome_cliente || '',
      codigos: [] as CodigoPeca[],
      idcliente: null,
      sale: sale,
      iduser: user?.id || null,
      sale_date_created: ''
    });
    setFoundClient(false);
    setGarantiaModalOpen(true);
  };

  const closeGarantiaModal = () => {
    setGarantiaModalOpen(false);
    setSaleForGarantia(null);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: '',
      codigos: [] as CodigoPeca[],
      idcliente: null,
      sale: {},
      iduser: null,
      sale_date_created: ''
    });
    setFoundClient(false);
    setIsSubmittingGarantia(false);
  };

  const manageWarranty = (sale: SaleWithUser) => {
    setSelectedSale(sale as Sale);
    if (sale.warranty_id > 0) {
      setWarrantyModalOpen(true);
    } else {
      openGarantiaModal(sale);
    }
  };

  const handleCpfChange = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    const formatted = cleanValue.length <= 11
      ? cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      : cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    setGarantiaFormData(prev => ({ ...prev, cpf_cliente: formatted }));
  };

  const handlePhoneChange = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    const formatted = cleanValue.length <= 10
      ? cleanValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      : cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    setGarantiaFormData(prev => ({ ...prev, telefone_cliente: formatted }));
  };

  const handleNameChange = (value: string) => {
    const formatted = value.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
    setGarantiaFormData(prev => ({ ...prev, nome_cliente: formatted }));
  };

  const searchClient = async (dado: string, valor: string) => {
    try {
      const response = await clientService.getClient(dado, valor);
      if (!response.id) {
        toast.info('Cliente não encontrado');
        return;
      }
      toast.success('Cliente encontrado!');
      setGarantiaFormData(prev => ({
        ...prev,
        idcliente: response.id,
        nome_cliente: response.nome_cliente,
        cpf_cliente: response.cpf_cliente,
        telefone_cliente: response.telefone_cliente?.replace('55', '').trim()
      }));
      setFoundClient(true);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast.error('Erro ao buscar cliente');
    }
  };

  const addCodigo = (codigo: string, tipo: string) => {
    const cleanCodigo = codigo.replace(/\D/g, '');
    if (cleanCodigo.length !== 13) {
      toast.error('Código inválido: deve ter 13 dígitos');
      return;
    }
    const codigoCompleto = `${cleanCodigo} (${tipo})`;
    if (garantiaFormData.codigos.some(c => c.code === codigoCompleto)) {
      toast.error('Código já adicionado');
      return;
    }
    if (garantiaFormData.codigos.length >= 10) {
      toast.error('Limite de 10 códigos atingido');
      return;
    }
    setGarantiaFormData(prev => ({
      ...prev,
      codigos: [{ id: Date.now(), code: codigoCompleto, edit: false }, ...prev.codigos]
    }));
  };

  const removeCodigo = (id: number) => {
    setGarantiaFormData(prev => ({
      ...prev,
      codigos: prev.codigos.filter(c => c.id !== id)
    }));
  };

  const submitGarantia = async () => {
    if (!garantiaFormData.nome_cliente || garantiaFormData.codigos.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsSubmittingGarantia(true);
    try {
      const telefoneClean = garantiaFormData.telefone_cliente.replace(/\D/g, '').trim();
      const garantiaData = {
        ...garantiaFormData,
        telefone_cliente: telefoneClean,
        iduser: user?.id,
        sale: saleForGarantia || {}
      };

      const response = await saleService.createGarantia(garantiaData);
      if (response?.success !== false) {
        toast.success('Garantia criada com sucesso!');

        // Atualizar venda na lista local
        if (response?.data) {
          const newSale = response.data;
          setSales(prev => prev.map(s => 
            s.id === newSale.id ? { ...s, warranty_id: newSale.warranty?.id, warranty: newSale.warranty } : s
          ));
        }
        closeGarantiaModal();
      } else {
        toast.error(`Erro ao criar garantia. ${response?.message || ''}`);
      }
    } catch (error) {
      console.error('Erro ao criar garantia:', error);
      toast.error('Erro ao criar garantia');
    } finally {
      setIsSubmittingGarantia(false);
    }
  };

  const downloadWarranty = async () => {
    if (!selectedSale) return;
    const element = document.getElementById('warranty-content');
    if (!element) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(element, { useCORS: true, allowTaint: true, background: '#e9e9e9' });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `Garantia_${selectedSale.codepagarme || `XDINF${selectedSale.id}`}.png`;
      link.click();
      toast.success('Certificado de garantia baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
      toast.error('Erro ao gerar certificado de garantia');
    }
  };

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Relatório de Vendas</CardTitle>
              <CardDescription>Exporte e visualize todas as vendas</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={atualizarDados} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-1" />
                Atualizar
              </Button>
              <Button onClick={exportExcel} variant="outline" size="sm">
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Excel
              </Button>
              <Button onClick={exportPdf} variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                PDF
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Busca */}
            <div className="flex-1 min-w-[250px]">
              <label className="text-sm font-medium mb-2 block">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Código, revendedora, cliente..."
                  value={searchFilter}
                  onChange={(e) => applyFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Data Início */}
            <div className="min-w-[180px]">
              <label className="text-sm font-medium mb-2 block">Data Início</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Data Fim */}
            <div className="min-w-[180px]">
              <label className="text-sm font-medium mb-2 block">Data Fim</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Ações */}
            <div className="flex gap-2">
              <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate} size="sm">
                Aplicar
              </Button>
              <Button onClick={limparDatas} variant="outline" size="sm">
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Vendas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Vendas ({totalSales})
            </CardTitle>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 por pág.</SelectItem>
                <SelectItem value="100">100 por pág.</SelectItem>
                <SelectItem value="150">150 por pág.</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-0 md:px-6">
          {/* Barra de ação em massa */}
          {user?.role === 'admin' && selectedIds.size > 0 && (
            <div className="flex items-center justify-between bg-primary/10 border border-primary/30 rounded-lg px-4 py-3 mb-4">
              <span className="text-sm font-medium">
                {selectedIds.size} venda(s) selecionada(s)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={isBulkLoading}
                >
                  <X className="h-4 w-4 mr-1" />
                  Limpar seleção
                </Button>
                <Button
                  size="sm"
                  className="bg-green-100 hover:bg-green-200 text-green-800 border border-green-300"
                  onClick={() => onBulkAction('baixado')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Baixar todas'}
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300"
                  onClick={() => onBulkAction('aprovado')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Aprovar todas'}
                </Button>
                <Button
                  size="sm"
                  className="bg-red-100 hover:bg-red-200 text-red-800 border border-red-300"
                  onClick={() => onBulkAction('reprovado')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Reprovar todas'}
                </Button>
                <Button
                  size="sm"
                  className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300"
                  onClick={() => onBulkAction('pendente')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Pendente todas'}
                </Button>
              </div>
            </div>
          )}

          {sales.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">Nenhuma venda encontrada</h3>
              <p className="text-sm text-muted-foreground">Tente ajustar os filtros.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      {user?.role === 'admin' && (
                        <th className="p-3 w-10">
                          <button onClick={toggleSelectAllPage} className="flex items-center justify-center">
                            {isAllPageSelected
                              ? <CheckSquare2 className="h-4 w-4 text-primary" />
                              : <Square className="h-4 w-4 text-muted-foreground" />}
                          </button>
                        </th>
                      )}
                      <th className="text-left p-3 font-semibold">Cód. Pagarme</th>
                      <th className="text-left p-3 font-semibold">Valor</th>
                      <th className="text-left p-3 font-semibold">Revendedora</th>
                      <th className="text-left p-3 font-semibold">
                        <Select
                          value={clientFilter || 'todos'}
                          onValueChange={(value) => {
                            const val = value === 'todos' ? '' : value;
                            setClientFilter(val);
                            setCurrentPage(1);
                            loadSales(1, pageSize, searchFilter, startDate, endDate, situationFilter, val);
                          }}
                        >
                          <SelectTrigger className="w-[140px] h-8 text-xs font-semibold border-none bg-transparent shadow-none px-0 gap-1">
                            <SelectValue placeholder="Cliente" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Cliente (Todos)</SelectItem>
                            <SelectItem value="cadastrados">Cadastrados</SelectItem>
                            <SelectItem value="sem_cadastro">Sem Cadastro</SelectItem>
                          </SelectContent>
                        </Select>
                      </th>
                      <th className="text-left p-3 font-semibold">Data</th>
                      <th className="text-left p-3 font-semibold">
                        <Select
                          value={situationFilter || 'todas'}
                          onValueChange={(value) => {
                            const val = value === 'todas' ? '' : value;
                            setSituationFilter(val);
                            setCurrentPage(1);
                            loadSales(1, pageSize, searchFilter, startDate, endDate, val, clientFilter);
                          }}
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs font-semibold border-none bg-transparent shadow-none px-0 gap-1">
                            <SelectValue placeholder="Acerto" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todas">Acerto (Todas)</SelectItem>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="baixado">Baixado</SelectItem>
                            <SelectItem value="aprovado">Aprovado</SelectItem>
                            <SelectItem value="reprovado">Reprovado</SelectItem>
                          </SelectContent>
                        </Select>
                      </th>
                      <th className="text-left p-3 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => (
                      <tr key={sale.id} className={cn("border-b hover:bg-muted/50 transition-colors", selectedIds.has(sale.id) && "bg-gray-100")}>
                        {user?.role === 'admin' && (
                          <td className="p-3 w-10">
                            <button onClick={() => toggleSelect(sale.id)} className="flex items-center justify-center">
                              {selectedIds.has(sale.id)
                                ? <CheckSquare2 className="h-4 w-4 text-primary" />
                                : <Square className="h-4 w-4 text-muted-foreground" />}
                            </button>
                          </td>
                        )}
                        <td className="p-3">{getDisplayCode(sale)}</td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold">
                              {formatCurrency(sale.amount)}
                              {sale.installments && sale.installments > 1 && (
                                <span className="text-xs text-muted-foreground ml-1">em {sale.installments}x</span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">{mapPaymentMethod(sale.payment_method)}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col cursor-pointer" onClick={() => sale.user?.id && window.open(`/v2/usuarios/${sale.user.id}`, '_blank')}>
                            <span className="text-primary hover:underline inline-flex items-center gap-1 flex-wrap">
                              <span className="break-all">{sale.nome_revendedora || '-'}</span>
                              {sale.diamonds_revendedora > 0 && (
                                <span className="text-xs font-bold text-purple-600 inline-flex items-center gap-0.5 shrink-0">
                                  {sale.diamonds_revendedora} <Gem className="h-3 w-3" />
                                </span>
                              )}
                            </span>
                            {sale.codUser && <span className="text-xs text-muted-foreground">#{sale.codUser}</span>}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="inline-flex items-center gap-1 flex-wrap">
                              <span className="break-all">{sale.nome_cliente || 'Não informado'}</span>
                              {sale.diamonds_cliente > 0 && (
                                <span className="text-xs font-bold text-purple-600 inline-flex items-center gap-0.5 shrink-0">
                                  {sale.diamonds_cliente} <Gem className="h-3 w-3" />
                                </span>
                              )}
                            </span>
                            {sale.client_id && Number(sale.client_id) > 0
                              ? <span className="text-xs text-muted-foreground">#{sale.client_id}</span>
                              : <span className="text-xs text-muted-foreground">S/ Cadastro</span>
                            }
                          </div>
                        </td>
                        <td className="p-3">{formatDate(sale.date_created)}</td>
                        <td className="p-3">
                          <Select
                            value={sale.situation || 'pendente'}
                            onValueChange={(value) => onSituacaoChange(sale.id, value)}
                          >
                            <SelectTrigger className={cn("w-[130px] text-xs font-semibold", getSituationStyles(sale.situation).badge)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pendente">pendente</SelectItem>
                              <SelectItem value="reprovado">reprovado</SelectItem>
                              <SelectItem value="aprovado">aprovado</SelectItem>
                              <SelectItem value="baixado">baixado</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Gerar Recibo"
                              onClick={() => { setSelectedSale(sale as Sale); setReceiptModalOpen(true); }}
                            >
                              <Receipt className="h-4 w-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={sale.warranty_id > 0 ? 'Ver Garantia' : 'Adicionar Garantia'}
                              onClick={() => manageWarranty(sale)}
                            >
                              <Shield className={cn("h-4 w-4", sale.warranty_id > 0 ? "text-green-500" : "text-yellow-500")} />
                            </Button>
                            {sale.payment_method === 'credit_card' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title="Ver detalhes"
                                disabled={!sale.idpagarme}
                                onClick={() => sale.idpagarme && navigate(`/relatorios-vendas/${sale.idpagarme}`)}
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 px-3">
                {sales.map((sale) => (
                  <Card key={sale.id} className={cn("transition-all", getSituationStyles(sale.situation).row)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-muted-foreground font-mono">{getDisplayCode(sale)}</span>
                        <Badge variant="outline" className={cn("text-xs ml-2 shrink-0", getSituationStyles(sale.situation).badge)}>
                          {sale.situation}
                        </Badge>
                      </div>

                      <div className="flex items-center justify-between mb-2">
                        <div className="flex flex-col">
                          <span className="font-bold text-green-600">{formatCurrency(sale.amount)}</span>
                          <span className="text-xs text-muted-foreground">
                            {mapPaymentMethod(sale.payment_method)}
                            {sale.installments && sale.installments > 1 ? ` · ${sale.installments}x` : ' · à vista'}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(sale.date_created)}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div className="flex flex-col cursor-pointer" onClick={() => sale.user?.id && window.open(`/v2/usuarios/${sale.user.id}`, '_blank')}>
                          <span className="text-primary hover:underline inline-flex items-center gap-1 flex-wrap">
                            <span className="break-all">{sale.nome_revendedora || '-'}</span>
                            {sale.diamonds_revendedora > 0 && (
                              <span className="font-bold text-purple-600 inline-flex items-center gap-0.5 shrink-0">
                                {sale.diamonds_revendedora} <Gem className="h-3 w-3" />
                              </span>
                            )}
                          </span>
                          {sale.codUser && <span className="text-muted-foreground">#{sale.codUser}</span>}
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="inline-flex items-center justify-end gap-1 flex-wrap">
                            <span className="break-all">{sale.nome_cliente || 'Não informado'}</span>
                            {sale.diamonds_cliente > 0 && (
                              <span className="font-bold text-purple-600 inline-flex items-center gap-0.5 shrink-0">
                                {sale.diamonds_cliente} <Gem className="h-3 w-3" />
                              </span>
                            )}
                          </span>
                          {sale.client_id && Number(sale.client_id) > 0
                            ? <span className="text-muted-foreground">#{sale.client_id}</span>
                            : <span className="text-muted-foreground">S/ Cadastro</span>
                          }
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t pt-3">
                        <Select
                          value={sale.situation || 'pendente'}
                          onValueChange={(value) => onSituacaoChange(sale.id, value)}
                        >
                          <SelectTrigger className={cn("w-[120px] text-xs font-semibold h-8", getSituationStyles(sale.situation).badge)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente">pendente</SelectItem>
                            <SelectItem value="reprovado">reprovado</SelectItem>
                            <SelectItem value="aprovado">aprovado</SelectItem>
                            <SelectItem value="baixado">baixado</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => { setSelectedSale(sale as Sale); setReceiptModalOpen(true); }}>
                            <Receipt className="h-4 w-4 text-primary" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => manageWarranty(sale)}>
                            <Shield className={cn("h-4 w-4", sale.warranty_id > 0 ? "text-green-500" : "text-yellow-500")} />
                          </Button>
                          {sale.payment_method === 'credit_card' && sale.idpagarme && (
                            <Button variant="ghost" size="icon" className="h-8 w-8"
                              onClick={() => navigate(`/relatorios-vendas/${sale.idpagarme}`)}>
                              <Eye className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-2 px-3">
                  <span className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalSales)} de {totalSales} vendas
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center text-sm px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modais */}
      {selectedSale && (
        <ReceiptModal
          isOpen={receiptModalOpen}
          onClose={() => setReceiptModalOpen(false)}
          selectedSale={selectedSale}
          userData={user}
          onDownloadReceipt={downloadReceipt}
        />
      )}

      {selectedSale && (
        <WarrantyModal
          isOpen={warrantyModalOpen}
          onClose={() => setWarrantyModalOpen(false)}
          selectedSale={selectedSale}
          userData={user}
          onDownloadWarranty={downloadWarranty}
        />
      )}

      <GarantiaModal
        isOpen={garantiaModalOpen}
        onClose={closeGarantiaModal}
        saleForGarantia={saleForGarantia}
        garantiaFormData={garantiaFormData}
        foundClient={foundClient}
        isSubmittingGarantia={isSubmittingGarantia}
        onCpfChange={handleCpfChange}
        onPhoneChange={handlePhoneChange}
        onNameChange={handleNameChange}
        onSearchClient={searchClient}
        onAddCodigo={addCodigo}
        onRemoveCodigo={removeCodigo}
        onSubmitGarantia={submitGarantia}
      />
    </div>
  );
}
